import { Badge, Icon } from "evergreen-ui"
import { capitalize, max, reverse, sum, flatten, sortBy } from "lodash"
import { interpolateOrRd, interpolateGnBu } from "d3-scale-chromatic"
import Figure from "./figure"

const neurons = [359, 379, 385, 343, 330, 340, 388, 342]
export default class Mechanics extends Component {
  state = {
    activeNeuron: 388,
    active3a: null,
    active3b: neurons[0],
    activeSpatialVec: [2, 2],
  }

  render() {
    const { activeSpatialVec, active3a, active3b } = this.state
    const { mechanics } = this.props.data
    const { mags, images, vecs } = mechanics[active3b]
    const maxMag = max(flatten(mags))
    const moduleBg = `rgba(255, 255, 255,0.8)`
    const neuronSize = 100
    const neuronImage = (layer, neuron) =>
      `https://storage.googleapis.com/fls/nickc/neuron_renders/${layer}_${neuron}.jpg`

    const neuronSize3a = 80

    const highlightColor = `rgba(228, 118, 88, 0.9)`

    const ModuleTitle = ({ children, subtitle, fontSize = 24 }) => (
      <Surface flexFlow="row" marginY={6}>
        <Text
          fontSize={fontSize}
          opacity={0.7}
          fontWeight={600}
          marginLeft={10}
        >
          {children}
        </Text>
        {subtitle && (
          <Text
            marginLeft={5}
            fontSize={fontSize}
            opacity={0.4}
            fontWeight={600}
            marginLeft={10}
          >
            {subtitle}
          </Text>
        )}
      </Surface>
    )

    const Module = ({ children, ...props }) => {
      return (
        <Surface
          margin={3}
          padding={3}
          background={moduleBg}
          borderRadius={8}
          boxShadow="0 30px 60px -12px rgba(50,50,93,.04), 0 18px 36px -18px rgba(0,0,0,.06), 0 -12px 36px -8px rgba(0,0,0,.025)"
          style={{ border: `2px solid rgba(0, 0, 0, 0.2)` }}
          flexFlow="row"
          flexWrap="wrap"
          justifyContent="center"
          alignItems="center"
          {...props}
        >
          {children}
        </Surface>
      )
    }

    const firstModules = [
      { name: "curves", neurons: [104, 92, 95, 145, 147, 137, 81, 71, 189] },
      { name: "edges", neurons: [134, 169, 69, 154, 101, 75] },
    ]

    const ZoomedImg = ({ src, size, padding, ...props }) => (
      <Surface
        width={size}
        height={size}
        overflow="hidden"
        borderRadius={5}
        position="relative"
        {...props}
      >
        <img
          src={src}
          width={size + padding * 2}
          height={size + padding * 2}
          style={{ position: "absolute", left: -padding, top: -padding }}
        />
      </Surface>
    )

    return (
      <Figure>
        <Surface
          flexFlow="row"
          alignItems="center"
          width="100%"
          marginY={20}
          justifyContent="center"
        >
          <Surface marginX={20}>
            {firstModules.map(({ name, neurons }) => (
              <Surface marginY={20}>
                <ModuleTitle subtitle="3a">{capitalize(name)}</ModuleTitle>
                <Module>
                  <Surface flexFlow="row" flexWrap="wrap" width={240}>
                    {neurons.map(index => (
                      <Surface
                        flexFlow="row"
                        alignItems="center"
                        onClick={() => {
                          if (active3a === index) {
                            this.setState({ active3a: null })
                          } else {
                            this.setState({ active3a: index })
                          }
                        }}
                        marginY={0}
                      >
                        <ZoomedImg
                          style={{
                            border: `2px solid ${
                              index === active3a
                                ? highlightColor
                                : `transparent`
                            }`,
                          }}
                          size={neuronSize3a}
                          src={neuronImage("mixed3a", index)}
                          padding={20}
                        />
                      </Surface>
                    ))}
                  </Surface>
                </Module>
              </Surface>
            ))}
          </Surface>
          <Surface marginX={20}>
            <Surface>
              <ModuleTitle
                subtitle={
                  <Surface flexFlow="row" alignItems="center">
                    3a <Icon icon="arrow-right" marginX={8} marginTop={2} /> 3b
                  </Surface>
                }
              >
                Decomposed Weights
              </ModuleTitle>
              <Module>
                <Surface
                  display="grid"
                  gridTemplateColumns={`repeat(${mags.length}, auto)`}
                  padding={3}
                  gridTemplateRows={`repeat(${mags.length}, auto)`}
                  gridGap={5}
                >
                  {images.map((row, rowIndex) =>
                    row.map((img, colIndex) => {
                      const size = 79

                      if (active3a) {
                        const fn =
                          vecs[rowIndex][colIndex][active3a] > 0
                            ? interpolateOrRd
                            : interpolateGnBu

                        const active =
                          activeSpatialVec[0] === rowIndex &&
                          activeSpatialVec[1] === colIndex

                        return (
                          <Surface
                            width={size}
                            height={size}
                            onMouseEnter={() =>
                              this.setState({
                                activeSpatialVec: [rowIndex, colIndex],
                              })
                            }
                            style={{
                              border: `2px solid ${
                                active ? `rgba(0, 0, 0, 0.4)` : `transparent`
                              }`,
                            }}
                            background={fn(mags[rowIndex][colIndex] / maxMag)}
                            borderRadius={5}
                          />
                        )
                      } else {
                        return (
                          <img
                            style={{
                              opacity: mags[rowIndex][colIndex] / maxMag,
                              borderRadius: 5,
                            }}
                            width={size}
                            height={size}
                            src={img}
                          />
                        )
                      }
                    }),
                  )}
                </Surface>
              </Module>
            </Surface>
          </Surface>
          <Surface marginX={20}>
            <ModuleTitle subtitle="3b">Curve Neurons</ModuleTitle>
            <Module>
              <Surface
                display="grid"
                gridTemplateColumns={`repeat(2, auto)`}
                gridTemplateRows={`repeat(4, auto)`}
                gridGap={5}
              >
                {[
                  neurons[1],
                  neurons[2],
                  neurons[0],
                  neurons[3],
                  neurons[4],
                  neurons[7],
                  neurons[5],
                  neurons[6],
                ].map(neuron => (
                  <Surface
                    style={{
                      border: `2px solid ${
                        neuron === active3b ? highlightColor : `transparent`
                      }`,
                    }}
                    width={neuronSize}
                    height={neuronSize}
                    onMouseEnter={() => this.setState({ active3b: neuron })}
                    borderRadius={5}
                    overflow="hidden"
                  >
                    <img
                      alt={`neuron 4b:${neuron}`}
                      width={neuronSize}
                      src={`https://storage.googleapis.com/fls/nickc/neuron_renders/mixed3b_${neuron}.jpg`}
                    />
                  </Surface>
                ))}
              </Surface>
            </Module>
          </Surface>
        </Surface>
      </Figure>
    )
  }
}
